import React, { useEffect, useState } from 'react';
import './upload-modal.css';
import { getCookieId } from '../../WebserverUser';
import {
  createServiceUpload,
  defaultServicePipeline,
  loadServiceCatalogue,
} from '../../../shared/xomics-webserver-client';

const SERVICE_CODE = 'oncorisk';

function UploadModal({ isOpen, onClose, cookieId: cookieIdProp, onUploadSuccess }) {
  const [uploadedFiles, setUploadedFiles] = useState({ vcf: null, dna: null, rna: null });
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState({});
  const [currentFile, setCurrentFile] = useState(null);
  const [uploadComplete, setUploadComplete] = useState(false);
  const [pipeline, setPipeline] = useState(null);
  const [sampleCode, setSampleCode] = useState('');

  const cookieId = cookieIdProp || getCookieId();

  useEffect(() => {
    if (!cookieId) return;
    loadServiceCatalogue(SERVICE_CODE, cookieId)
      .then((catalogue) => {
        const selected = defaultServicePipeline(catalogue);
        setPipeline(selected);
        if (selected.sample_codes?.length === 1) setSampleCode(selected.sample_codes[0]);
      })
      .catch((error) => console.error('[UploadModal] pipeline catalogue failed', error));
  }, [cookieId]);

  const handleFileChange = (type, e) => {
    const file = e.target.files[0];
    if (file) {
      setUploadedFiles(prev => ({ ...prev, [type]: file }));
    }
  };

  const handleClear = () => {
    setUploadedFiles({ vcf: null, dna: null, rna: null });
  };

  const handleSubmit = async () => {
    const vcf = uploadedFiles.vcf;
    if (!vcf || !pipeline || !sampleCode) return;
    setIsUploading(true);
    setUploadProgress({});
    setUploadComplete(false);
    let successful = false;
    
    try {
      setCurrentFile(vcf.name);
      await createServiceUpload({
        serviceCode: SERVICE_CODE,
        token: cookieId,
        pipeline,
        sampleCode,
        selectedFiles: [{ file: vcf, slotPosition: 1, label: vcf.name }],
        displayName: vcf.name,
        metadata: { original_files: [vcf.name] },
        onProgress: ({ fraction }) => setUploadProgress({
          [vcf.name]: Math.round(fraction * 100),
        }),
      });
      setUploadComplete(true);
      successful = true;
      
      // Call the callback if provided
      if (onUploadSuccess) {
        onUploadSuccess();
      }
    } catch (err) {
      console.error('[UploadModal] upload error', err);
      setUploadProgress({ [vcf.name]: 0 });
      alert(err.message);
    } finally {
      setIsUploading(false);
    }

    if (successful) handleClear();

    // Automatically close the modal only after the upload was completed.
    if (successful && onClose) {
      setTimeout(() => onClose(), 1000); // Small delay to show completion
    }
  };

  if (!isOpen) return null;

  return (
    <div className="OCRP-upload-modal-overlay" onClick={isUploading ? null : onClose}>
      <div className="OCRP-upload-modal" onClick={e => e.stopPropagation()}>
        <div className="OCRP-upload-modal-header">
          <div className="OCRP-upload-title">Upload Genomic Files</div>
          <button className="OCRP-upload-close" onClick={isUploading ? null : onClose} disabled={isUploading} style={isUploading ? {opacity: 0.5, cursor: 'not-allowed'} : {}}>
            <i className="fas fa-times"></i>
          </button>
        </div>
        <p className="OCRP-upload-description">
          Upload tumour variant calls in VCF or VCF.GZ format for the active OncoRisk pipeline.
        </p>

        <div className="OCRP-upload-grid">
          <div className="OCRP-upload-card">
            <div className="OCRP-upload-icon">
              <i className="fas fa-file-medical"></i>
            </div>
            <h3>Tumour VCF</h3>
            <p>Upload the variant calls to analyze.</p>
            <label className="OCRP-file-upload">
              <input type="file" accept=".vcf,.vcf.gz,.gz" onChange={e => handleFileChange('vcf', e)} />
              <span>{uploadedFiles.vcf ? uploadedFiles.vcf.name : 'Choose File'}</span>
            </label>
            {uploadedFiles.vcf && (
              <div className="OCRP-file-details">
                <span>Size: {(uploadedFiles.vcf.size / (1024 * 1024)).toFixed(2)} MB</span>
              </div>
            )}
          </div>

     
        </div>
        {(pipeline?.sample_codes?.length || 0) > 1 && (
          <div className="OCRP-upload-options">
            <label htmlFor="oncorisk-sample-type">Tumour sample type</label>
            <select
              id="oncorisk-sample-type"
              value={sampleCode}
              onChange={(event) => setSampleCode(event.target.value)}
              disabled={isUploading}
            >
              <option value="">Select...</option>
              {pipeline.sample_codes.map((code) => (
                <option key={code} value={code}>{code}</option>
              ))}
            </select>
          </div>
        )}
        <div className="OCRP-upload-options">
          <div className="OCRP-analysis-params">
            {/* Analysis parameters can be added here in the future */}
          </div>
          
          {isUploading && (
            <div className="OCRP-upload-progress-container">
              <h3>Upload Progress</h3>
              <div className="OCRP-current-file">
                <span>Current file: {currentFile}</span>
              </div>
              
              {Object.entries(uploadProgress).map(([filename, progress]) => (
                <div key={filename} className="OCRP-file-progress">
                  <div className="OCRP-file-progress-name">{filename}</div>
                  <div className="OCRP-progress-bar">
                    <div 
                      className="OCRP-progress-fill" 
                      style={{ width: `${progress}%` }}
                    ></div>
                  </div>
                  <div className="OCRP-progress-percentage">{progress}%</div>
                </div>
              ))}
              
              {uploadComplete && (
                <div className="OCRP-upload-complete">
                  <i className="fas fa-check-circle"></i> Upload complete! Processing...
                </div>
              )}
            </div>
          )}
        </div>
        <div className="OCRP-upload-privacy">
          <div className="OCRP-privacy-header">
            <i className="fas fa-shield-alt"></i>
            <span>Data Privacy & Security</span>
          </div>
          <ul className="OCRP-privacy-list">
            <li><i className="fas fa-fingerprint"></i>All uploads are anonymized using unique identifiers (UUID)</li>
            <li><i className="fas fa-lock"></i>Data is encrypted in transit and at rest using industry-standard protocols</li>
            <li><i className="fas fa-ban"></i>Your data is never sold, shared, or used for purposes other than your own analysis session</li>
          </ul>
        </div>
        <div className="OCRP-upload-actions">
          <button className="OCRP-button OCRP-button-secondary" onClick={handleClear}>Clear All</button>
          <button
            className="OCRP-button OCRP-button-primary"
            onClick={handleSubmit}
            disabled={!uploadedFiles.vcf || !pipeline || !sampleCode || isUploading}
          >
            {isUploading ? 'Uploading...' : 'Submit Analysis'}
          </button>
        </div>
      </div>
    </div>
  );
}

export default UploadModal;
